import { useMemo, useState } from "react";
import type { CaseRecord, RecordFact } from "../../types";
import { api } from "../../api";
import { formatFactDate } from "../../lib/factMeta";
import {
  partyContacts,
  type PartyCard as Party,
  type PartyEvidence,
  type PartyUi,
} from "../../lib/parties";
import type { CommitmentRow as CommitmentRowData } from "../../lib/commitments";
import { useModalDismiss } from "../../lib/useModalDismiss";
import { CommitmentRowView } from "./CommitmentsTable";
import FactCard from "./FactCard";

// Party detail panel (Round 10): side panel on desktop / bottom sheet on
// mobile — same component family as quote-in-context. Everything shown is
// deterministic: the gated card, dates derived from the party's facts,
// contact details from record metadata and signature evidence windows
// (each citing its source), their commitment rows (SHARED state with the
// main table — one source of truth), and their facts.
export default function PartyPanel({
  party,
  caseId,
  facts,
  records,
  recordById,
  evidence,
  isMine,
  commitmentRows,
  onToggleResolved,
  onNavigateToRecord,
  onViewQuote,
  partyUi,
  onClose,
}: {
  party: Party;
  caseId: string;
  facts: RecordFact[];
  records: CaseRecord[];
  recordById: Map<string, CaseRecord>;
  evidence?: PartyEvidence;
  // Whether a raw stored party value resolves to THIS party (canonical
  // matching via the party index — not string equality).
  isMine: (raw: string | null) => boolean;
  commitmentRows: CommitmentRowData[];
  onToggleResolved: (key: string, resolved: boolean) => void;
  onNavigateToRecord?: (recordId: string) => void;
  onViewQuote?: (fact: RecordFact) => void;
  partyUi?: PartyUi;
  onClose: () => void;
}) {
  useModalDismiss(onClose);
  const [contactState, setContactState] = useState<"idle" | "saving" | "added" | "failed">("idle");

  const mine = useMemo(
    () => facts.filter((f) => isMine(f.party)),
    [facts, isMine],
  );
  const dates = useMemo(
    () => mine.map((f) => f.date).filter((d): d is string => !!d).sort(),
    [mine],
  );
  const contacts = useMemo(
    () => partyContacts(party.name, records, evidence),
    [party.name, records, evidence],
  );
  const myCommitments = useMemo(
    () => commitmentRows.filter((r) => isMine(r.fact.party)),
    [commitmentRows, isMine],
  );
  const byTypeNewest = (type: string) =>
    mine
      .filter((f) => f.fact_type === type)
      .sort((a, b) => ((a.date ?? "") < (b.date ?? "") ? 1 : -1));
  const decisions = useMemo(() => byTypeNewest("decision"), [mine]);
  const requests = useMemo(() => byTypeNewest("request"), [mine]);

  async function addToContacts() {
    setContactState("saving");
    try {
      await api.createContact(caseId, {
        name: party.name,
        role: party.role ?? null,
        email: contacts.find((c) => c.kind === "email")?.value ?? null,
        phone: contacts.find((c) => c.kind === "phone")?.value ?? null,
        notes: party.org ? `Affiliation: ${party.org}` : null,
      });
      setContactState("added");
    } catch {
      setContactState("failed");
    }
  }

  const sourceLabel = (recordId: string | null, kind: "metadata" | "signature") => {
    const rec = recordId ? recordById.get(recordId) : undefined;
    const title = rec?.subject?.trim() || "record";
    return kind === "signature" ? `from signature, ${title}` : `from record metadata, ${title}`;
  };

  const renderFacts = (list: RecordFact[]) =>
    list.map((f) => (
      <FactCard
        key={f.id}
        fact={f}
        record={recordById.get(f.record_id)}
        onNavigateToRecord={onNavigateToRecord}
        onViewQuote={onViewQuote}
        recordById={recordById}
      />
    ));

  return (
    <div
      className="quote-context-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Party: ${party.name}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="quote-context-panel party-panel">
        <div className="quote-context-head">
          <div>
            <strong>{party.name}</strong>{" "}
            {(party.org || party.role) && (
              <span className="overview-muted">
                {[party.org, party.role].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
          <div className="quote-context-actions">
            <button type="button" className="fact-source-link" onClick={onClose}>
              Close ✕
            </button>
          </div>
        </div>

        {dates.length > 0 && (
          <p className="overview-muted party-panel-dates">
            First appearance {formatFactDate(dates[0]!)} · last{" "}
            {formatFactDate(dates[dates.length - 1]!)} · {mine.length} fact
            {mine.length === 1 ? "" : "s"}
          </p>
        )}

        {contacts.length > 0 && (
          <section className="party-panel-section">
            <h4 className="overview-section-title">Contact</h4>
            <ul className="party-contact-list">
              {contacts.map((c) => (
                <li key={`${c.kind}:${c.value}`}>
                  {c.kind === "email" ? "✉" : "☎"} {c.value}{" "}
                  <span className="overview-muted">
                    (
                    {c.recordId && onNavigateToRecord ? (
                      <button
                        type="button"
                        className="fact-source-link"
                        onClick={() => onNavigateToRecord(c.recordId!)}
                      >
                        {sourceLabel(c.recordId, c.sourceKind)}
                      </button>
                    ) : (
                      sourceLabel(c.recordId, c.sourceKind)
                    )}
                    )
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn-secondary party-add-contact"
              disabled={contactState === "saving" || contactState === "added"}
              onClick={() => void addToContacts()}
            >
              {contactState === "added"
                ? "Added to contacts ✓"
                : contactState === "failed"
                  ? "Add to contacts (retry)"
                  : "Add to contacts"}
            </button>
          </section>
        )}

        {myCommitments.length > 0 && (
          <section className="party-panel-section">
            <h4 className="overview-section-title">Their commitments</h4>
            <div className="table-scroll">
            <table className="fact-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Commitment</th>
                  <th>Deadline</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {myCommitments.map((row) => (
                  <CommitmentRowView
                    key={row.fact.id}
                    row={row}
                    recordById={recordById}
                    onNavigateToRecord={onNavigateToRecord}
                    onToggleResolved={onToggleResolved}
                    partyUi={partyUi}
                    showParty={false}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </section>
        )}

        {decisions.length > 0 && (
          <section className="party-panel-section">
            <h4 className="overview-section-title">Their decisions</h4>
            {renderFacts(decisions)}
          </section>
        )}

        {requests.length > 0 && (
          <section className="party-panel-section">
            <h4 className="overview-section-title">Their requests</h4>
            {renderFacts(requests)}
          </section>
        )}

        {mine.length > 0 && (
          <details className="fact-components party-panel-all">
            <summary>
              All facts ({mine.length})
            </summary>
            {renderFacts(mine)}
          </details>
        )}
      </div>
    </div>
  );
}
